import { useQuery } from '@tanstack/react-query'
import { fetchApiWithToken } from '~/api/utils'
import type {
  ApplicationInfosLDResponse,
  ApplicationInfosResponse
} from '../types'

const ID_CHUNK_SIZE = 100

/**
 * Lightweight metadata for a bulk selection: one collection request, no attachment content and no
 * per-application S3 round-trip. Feeds the candidate selector, the recipient count and the shared
 * offer detection. The heavy attachment content is fetched lazily, one candidate at a time, through
 * useApplication.
 */
export const useApplicationsHead = (
  applicationIds: number[]
): {
  applications: ApplicationInfosResponse[]
  isLoading: boolean
} => {
  const sortedIds = [...applicationIds].sort((a, b) => a - b)

  const { data, isLoading } = useQuery({
    queryKey: ['applications', 'head', sortedIds],
    queryFn: async () => {
      const chunks: number[][] = []
      for (let i = 0; i < sortedIds.length; i += ID_CHUNK_SIZE) {
        chunks.push(sortedIds.slice(i, i + ID_CHUNK_SIZE))
      }

      const pages = await Promise.all(
        chunks.map((chunk) => {
          const params = new URLSearchParams()
          for (const id of chunk) {
            params.append('id[]', String(id))
          }
          params.set('isArchived', 'all')
          params.set('isSpam', 'all')
          params.set('itemsPerPage', String(chunk.length))

          return fetchApiWithToken<ApplicationInfosLDResponse>({
            method: 'get',
            url: `/v1/applications?${params}`,
            useLDJson: true
          })
        })
      )

      return pages.flatMap((page) => page['hydra:member'])
    },
    enabled: sortedIds.length > 0,
    staleTime: 60_000
  })

  const members: ApplicationInfosResponse[] = data ?? []

  return {
    applications: applicationIds
      .map((id) => members.find((application) => application.id === id))
      .filter((application) => application !== undefined),
    isLoading
  }
}
